import { useState } from 'react';

import { truncate } from 'lodash-es';
import { InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface ContextVariable {
  name: string;
  value: string;
  type?: 'text' | 'number' | 'code';
}

interface ContextVariablesPanelProps {
  variables: ContextVariable[];
  onRefresh?: () => void;
  tooltipText?: string;
  hideRefreshButton?: boolean;
}

const TRUNCATE_LENGTH = 100;

export default function ContextVariablesPanel({
  variables,
  onRefresh,
  tooltipText = 'These variables provide context for your webhook configuration',
  hideRefreshButton = false,
}: ContextVariablesPanelProps) {
  const { t } = useTranslation();
  const [expandedVariables, setExpandedVariables] = useState<Set<string>>(new Set());

  const toggleExpanded = (variableName: string) => {
    const newExpanded = new Set(expandedVariables);
    if (newExpanded.has(variableName)) {
      newExpanded.delete(variableName);
    } else {
      newExpanded.add(variableName);
    }
    setExpandedVariables(newExpanded);
  };

  const shouldTruncate = (text: string) => text.length > TRUNCATE_LENGTH;

  return (
    <div className="flex max-h-[512px] flex-col overflow-hidden rounded-lg border border-border bg-background">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-border bg-subtle px-6 py-4">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-foreground">
            {t('auto_complete.group_labels.context_variable')}
          </div>
          <InfoTooltip content={tooltipText} delayDuration={0}>
            <InfoIcon className="h-4 w-4 cursor-help text-muted-foreground" />
          </InfoTooltip>
        </div>
        {!hideRefreshButton && (
          <InfoTooltip content={t('webhook.test.refresh_tooltip')} delayDuration={0}>
            <Button variant="ghost" size="sm" onClick={onRefresh} className="h-auto p-0 text-sm">
              {t('webhook.test.update_variables')}
            </Button>
          </InfoTooltip>
        )}
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-6">
        {variables.map((variable) => {
          const isExpanded = expandedVariables.has(variable.name);
          const needsTruncation = shouldTruncate(variable.value);
          const displayValue =
            isExpanded || !needsTruncation
              ? variable.value
              : truncate(variable.value, { length: TRUNCATE_LENGTH, separator: ' ' });

          return (
            <div key={variable.name}>
              <div className="mb-2 text-sm font-semibold text-foreground">{variable.name}</div>
              <div className="text-xs leading-relaxed text-foreground">
                <div
                  className={cn(
                    'wrap-anywhere whitespace-pre-wrap',
                    variable.type === 'code' && 'font-mono'
                  )}
                >
                  {displayValue}
                </div>
                {needsTruncation && (
                  <button
                    type="button"
                    onClick={() => toggleExpanded(variable.name)}
                    className="mt-1 block text-xs text-primary hover:underline"
                  >
                    {isExpanded ? t('common.show_less') : t('common.show_more')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
